import { useTranslation } from 'react-i18next';
import { band, type AxisContent } from '@neke/core';

interface Props {
  content: AxisContent;
  value: number;
  partnerValue?: number;
  partnerName?: string;
  note?: string;
}

/**
 * Ось словами, без шкалы. Полоска с точками читалась как оценка и не давала
 * понять, чья точка чья, — поэтому позиция описывается фразой, а расхождение
 * пары — одним словом и цветом зон отчёта.
 */
const GAP = [
  { max: 15, tone: 'green', key: 'same' },
  { max: 35, tone: 'yellow', key: 'close' },
  { max: 100, tone: 'red', key: 'far' },
] as const;

function gap(delta: number) {
  return GAP.find((g) => delta <= g.max) ?? GAP[GAP.length - 1];
}

export function AxisRow({ content, value, partnerValue, partnerName, note }: Props) {
  const { t } = useTranslation();

  const lean = (v: number) => {
    const b = band(v);
    // «Посередине» само по себе ни о чём: без названий полюсов непонятно,
    // посередине между чем именно стоит человек.
    if (b === 'mid') return t('axis.leanMid', { left: content.left, right: content.right });
    return t('axis.lean', { pole: b === 'lo' ? content.left : content.right });
  };

  const verdict = partnerValue === undefined ? null : gap(Math.abs(value - partnerValue));

  return (
    <div className="axis">
      <div className="axis-head">
        <span className="axis-name">{content.name}</span>
        {verdict && <span className={`verdict ${verdict.tone}`}>{t(`axis.${verdict.key}`)}</span>}
      </div>

      <div className="axis-sides">
        <p className="side">
          <span className="who">{t('axis.you')}</span>
          <span>{lean(value)}</span>
        </p>
        {partnerValue !== undefined && (
          <p className="side">
            <span className="who">{partnerName}</span>
            <span>{lean(partnerValue)}</span>
          </p>
        )}
      </div>

      {note && <p className="fine">{note}</p>}
    </div>
  );
}
